import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material'
import {
  History,
  Create,
  Update,
  Delete,
  Login,
  Logout,
  Lock,
  VpnKey
} from '@mui/icons-material'
import axios from 'axios'

const ActividadUsuario = ({ usuarioId, usuarioNombre }) => {
  const [actividad, setActividad] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [limit, setLimit] = useState(50)

  useEffect(() => {
    if (usuarioId) {
      loadActividad()
    }
  }, [usuarioId, limit])

  const loadActividad = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('sgc_token')

      const response = await axios.get(`/api/auditoria/usuario/${usuarioId}`, {
        params: { limit },
        headers: { Authorization: `Bearer ${token}` }
      })

      setActividad(response.data.activity || [])
    } catch (err) {
      console.error('Error cargando actividad:', err)
      setError('Error al cargar la actividad del usuario')
    } finally {
      setLoading(false)
    }
  }

  const getAccionIcon = (accion) => {
    const icons = {
      CREATE: <Create fontSize="small" />,
      INSERT: <Create fontSize="small" />,
      UPDATE: <Update fontSize="small" />,
      DELETE: <Delete fontSize="small" />,
      LOGIN: <Login fontSize="small" />,
      LOGOUT: <Logout fontSize="small" />,
      CIERRE_SESION: <Logout fontSize="small" />,
      CIERRE_SESION_FORZADO: <Lock fontSize="small" />,
      CIERRE_TODAS_SESIONES: <Lock fontSize="small" />,
      PASSWORD_RESET: <VpnKey fontSize="small" />,
      PASSWORD_CHANGE: <VpnKey fontSize="small" />,
      LOGIN_FAILED: <Lock fontSize="small" />
    }
    return icons[accion] || <History fontSize="small" />
  }

  const getAccionColor = (accion) => {
    const colors = {
      CREATE: 'success',
      INSERT: 'success',
      UPDATE: 'info',
      DELETE: 'error',
      LOGIN: 'success',
      LOGOUT: 'default',
      CIERRE_SESION: 'default',
      CIERRE_SESION_FORZADO: 'warning',
      CIERRE_TODAS_SESIONES: 'warning',
      PASSWORD_RESET: 'warning',
      PASSWORD_CHANGE: 'info',
      LOGIN_FAILED: 'error',
      ACCESS: 'info'
    }
    return colors[accion] || 'default'
  }

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A'
    const date = new Date(fecha)
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatTabla = (tabla) => {
    const nombres = {
      procedimientos: 'Procedimientos',
      gestiones: 'Gestiones',
      usuarios: 'Usuarios',
      user_sessions: 'Sesiones',
      auditoria: 'Auditoría',
      normatividad: 'Normatividad',
      procesos: 'Procesos',
      definiciones: 'Definiciones',
      comentarios: 'Comentarios',
      roles_responsabilidad: 'Roles',
      diagramas_guardados: 'Diagramas'
    }
    return nombres[tabla] || tabla
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <History />
          Actividad de {usuarioNombre}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Registros</InputLabel>
          <Select
            value={limit}
            label="Registros"
            onChange={(e) => setLimit(e.target.value)}
          >
            <MenuItem value={25}>Últimos 25</MenuItem>
            <MenuItem value={50}>Últimos 50</MenuItem>
            <MenuItem value={100}>Últimos 100</MenuItem>
            <MenuItem value={200}>Últimos 200</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {actividad.length === 0 ? (
        <Alert severity="info">
          No hay actividad registrada para este usuario
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha/Hora</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Tabla</TableCell>
                <TableCell>Registro ID</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Notas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actividad.map((act, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {formatFecha(act.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getAccionIcon(act.accion)}
                      label={act.accion}
                      size="small"
                      color={getAccionColor(act.accion)}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {formatTabla(act.tabla)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {act.registro_id || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {act.ip_address || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', maxWidth: 200 }}>
                      {act.notas ? act.notas.substring(0, 50) : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Total de registros: {actividad.length}
        </Typography>
      </Box>
    </Box>
  )
}

export default ActividadUsuario
